// @cursor:state-management
// @cursor:storage
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Draft } from '../core/models';
import { getStore } from '../storage';
import { StoreError } from '../storage/Store';
import { migrateAllDrafts } from '../core/migrate';
import { extractPlainText } from '../core/lexical';
import { PublishedPost } from '../hooks/usePublishedPosts';
import { supabase } from '../lib/supabase';
// import { supabase } from '../lib/supabase';

// Helper to create empty Lexical state
const createEmptyLexicalState = () => JSON.stringify({
  root: {
    children: [{
      children: [],
      direction: null,
      format: "",
      indent: 0,
      type: "paragraph",
      version: 1
    }],
    direction: null,
    format: "",
    indent: 0,
    type: "root",
    version: 1
  }
});

// Helper to create Lexical state from plain text
const createLexicalState = (text: string) => JSON.stringify({
  root: {
    children: [{
      children: [{
        detail: 0,
        format: 0,
        mode: "normal",
        style: "",
        text,
        type: "text",
        version: 1
      }],
      direction: null,
      format: "",
      indent: 0,
      type: "paragraph",
      version: 1
    }],
    direction: null,
    format: "",
    indent: 0,
    type: "root",
    version: 1
  }
});

function createDraftId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);

    // Format random bytes as an RFC 4122 v4 UUID when randomUUID is unavailable.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  return `draft-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

interface DraftContextType {
  currentDraft: Draft | null;
  savedDrafts: Draft[];
  isNewDraft: boolean;
  areDraftsLoading: boolean;
  syncState: 'idle' | 'syncing' | 'error' | 'refreshed';
  retryLastSync: () => void;
  saveDraft: (lexicalState: string, platforms: { twitter: boolean; bluesky: boolean; farcaster: boolean }) => void;
  loadDraft: (id: string) => Promise<void>;
  loadDraftFromRoute: (id?: string) => Promise<void>;
  deleteDraft: (id: string) => Promise<void>;
  clearCurrentDraft: () => void;
  clearAllDrafts: () => Promise<void>;
  createNewDraft: () => string;
  createDraftWithText: (text: string) => string;
  createReplyOrQuoteDraft: (post: PublishedPost, mode: 'reply' | 'quote') => string;
  exportDrafts: () => Promise<void>;
  importDrafts: (importedDrafts: Draft[]) => Promise<void>;
  // Linked post functionality (reply/quote)
  linkedPost: PublishedPost | null;
  linkedPostMode: 'reply' | 'quote' | null;
  setLinkedPost: (post: PublishedPost, mode: 'reply' | 'quote') => void;
  clearLinkedPost: () => void;
  toggleLinkedPostMode: () => void;
}

const DraftContext = createContext<DraftContextType | undefined>(undefined);

export function useDrafts() {
  const context = useContext(DraftContext);
  if (context === undefined) {
    throw new Error('useDrafts must be used within a DraftProvider');
  }
  return context;
}

interface DraftProviderProps {
  children: ReactNode;
}

export function DraftProvider({ children }: DraftProviderProps) {
  const [currentDraft, setCurrentDraft] = useState<Draft | null>(null);
  const [savedDrafts, setSavedDrafts] = useState<Draft[]>([]);
  const [isNewDraft, setIsNewDraft] = useState(true);
  const [areDraftsLoading, setAreDraftsLoading] = useState(true);
  const [syncState, setSyncState] = useState<'idle' | 'syncing' | 'error' | 'refreshed'>('idle');
  const [lastSyncId, setLastSyncId] = useState<string | null>(null);
  // Linked post state for reply/quote functionality
  const [linkedPost, setLinkedPostState] = useState<PublishedPost | null>(null);
  const [linkedPostMode, setLinkedPostModeState] = useState<'reply' | 'quote' | null>(null);

  // Helper to build API URL
  const buildApiUrl = (path: string) => {
    const base = process.env.REACT_APP_API_URL;
    const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname);
    if (isLocalHost) {
      return `/api${path}`;
    }
    return base ? `${base.replace(/\/$/, '')}/api${path}` : `/api${path}`;
  };
  // Retain for potential future use (auth-reactive behaviors); not currently used.
  // const [authUserId, setAuthUserId] = useState<string | null>(null);

  // Migration: run once on mount
  useEffect(() => {
    const migrateLegacyDrafts = async () => {
      // Only run if not already migrated
      if (localStorage.getItem('litewrite-migrated')) return;
      const oldDraftsJson = localStorage.getItem('savedDrafts');
      if (!oldDraftsJson) return;
      try {
        const oldDrafts = JSON.parse(oldDraftsJson);
        // Only migrate if old format detected
        if (Array.isArray(oldDrafts) && oldDrafts.some(d => d.content || d.lastModified)) {
          const migrated = await migrateAllDrafts(oldDrafts);
          const store = await getStore();
          await Promise.all(migrated.map(draft => store.put(draft)));
          localStorage.removeItem('savedDrafts');
          localStorage.setItem('litewrite-migrated', '1');
          console.log('[Migration] Migrated legacy drafts:', migrated);
        }
      } catch (e) {
        console.error('[Migration] Error migrating legacy drafts:', e);
      }
    };
    migrateLegacyDrafts();
  }, []);

  // Note: if we need auth-reactive behaviors in the future, re-enable authUserId tracking.

  const createNewDraft = useCallback(() => {
    const isNewUser = savedDrafts.length === 0;
    console.log('[Drafts] createNewDraft called', { isNewUser, savedDraftsLength: savedDrafts.length });
    const onboardingContent = `Welcome to LiteWrite! 👋

TLDR: Use LiteWrite immediately, no account required. Create an account later if you want cloud sync + OAuth posting.

What you get out of the box (no account needed):
• Write in a focused editor with live character limit feedback
• Keep drafts saved on this device (works offline)
• Preview before posting
• Open one-click intent windows for X, Bluesky, and Farcaster
• Export and import drafts manually

What you unlock with an account:
• Cloud-synced drafts across devices
• Connect social accounts and choose OAuth or Intent posting per platform
• Upload images for posts
• Use Clarity editing in post preview
• Keep using intent posting whenever you prefer
• Save account-level settings (theme, feed visibility, platform preferences)
`

    const newDraft: Draft = {
      id: createDraftId(),
      raw: isNewUser ? createLexicalState(onboardingContent) : createEmptyLexicalState(),
      plainText: isNewUser ? onboardingContent : '',
      updated: Date.now(),
      platforms: {
        twitter: true,
        bluesky: true,
        farcaster: true
      }
    };
    setCurrentDraft(newDraft);
    setIsNewDraft(true);
    // Optimistically reflect in list so guards/UI do not block
    setSavedDrafts(prev => [newDraft, ...prev]);
    // Persist immediately so the route can safely navigate to it
    getStore()
      .then(store => store.put(newDraft))
      .then(() => console.log('[Drafts] persisted new draft', { id: newDraft.id }))
      .catch(e => console.error('[Drafts] error persisting new draft', e));
    return newDraft.id;
  }, [savedDrafts.length]);

  // Create a new draft with prefilled text content
  const createDraftWithText = useCallback((text: string) => {
    console.log('[Drafts] createDraftWithText called', { textLength: text.length });

    const newDraft: Draft = {
      id: createDraftId(),
      raw: createLexicalState(text),
      plainText: text,
      updated: Date.now(),
      platforms: {
        twitter: true,
        bluesky: true,
        farcaster: true
      }
    };
    setCurrentDraft(newDraft);
    setIsNewDraft(true);
    // Optimistically reflect in list
    setSavedDrafts(prev => [newDraft, ...prev]);
    // Persist immediately
    getStore()
      .then(store => store.put(newDraft))
      .then(() => console.log('[Drafts] persisted draft with text', { id: newDraft.id }))
      .catch(e => console.error('[Drafts] error persisting draft with text', e));
    return newDraft.id;
  }, []);

  // Create a new draft specifically for replying to or quoting a post
  const createReplyOrQuoteDraft = useCallback((post: PublishedPost, mode: 'reply' | 'quote') => {
    console.log('[Drafts] createReplyOrQuoteDraft called', { postId: post.id, mode });

    const newDraft: Draft = {
      id: createDraftId(),
      raw: createEmptyLexicalState(),
      plainText: '',
      updated: Date.now(),
      platforms: {
        twitter: true,
        bluesky: true,
        farcaster: true
      },
      linkedPostId: post.id,
      linkedPostMode: mode,
    };

    // Set the draft as current
    setCurrentDraft(newDraft);
    setIsNewDraft(true);

    // Also set the linked post state immediately so UI shows it
    setLinkedPostState(post);
    setLinkedPostModeState(mode);

    // Optimistically reflect in list
    setSavedDrafts(prev => [newDraft, ...prev]);

    // Persist immediately
    getStore()
      .then(store => store.put(newDraft))
      .then(() => console.log('[Drafts] persisted reply/quote draft', { id: newDraft.id, linkedPostId: post.id, mode }))
      .catch(e => console.error('[Drafts] error persisting reply/quote draft', e));

    return newDraft.id;
  }, []);

  // Load saved drafts on mount and when store changes
  useEffect(() => {
    const loadDrafts = async () => {
      try {
        console.log('[Drafts] loadDrafts: start');
        setAreDraftsLoading(true);
        const store = await getStore();
        const drafts = await store.list();
        setSavedDrafts(drafts);
        console.log('[Drafts] loadDrafts: loaded', { count: drafts.length });
        
        // With route-based editing, do not auto-create a draft here.
      } catch (error) {
        console.error('Error loading drafts:', error);
      } finally {
        setAreDraftsLoading(false);
        console.log('[Drafts] loadDrafts: loading=false');
      }
    };

    loadDrafts();

    // Subscribe to store changes
    let unsubscribe: (() => void) | undefined;
    getStore().then(store => {
      unsubscribe = store.onChange(() => {
        loadDrafts();
      });
    });

    return () => {
      unsubscribe?.();
    };
  }, [createNewDraft]);

  // First-time user: create onboarding draft AFTER drafts finish loading
  useEffect(() => {
    try {
      const debug = { areDraftsLoading, savedDraftsLen: savedDrafts.length };
      console.log('[Starter] check (auth-agnostic)', debug);
      if (areDraftsLoading) { console.log('[Starter] skip: drafts still loading'); return; }
      if (savedDrafts.length > 0) { console.log('[Starter] skip: already has drafts'); return; }
      console.log('[Starter] creating onboarding draft');
      // Create onboarding draft; DraftProvider will persist it to the current store (IDB or Supabase)
      createNewDraft();
    } catch (e) {
      console.error('[Starter] Failed to create onboarding draft:', e);
    }
  }, [areDraftsLoading, savedDrafts.length, createNewDraft]);

  // Load linked post data when currentDraft has a linkedPostId
  useEffect(() => {
    console.log('[LinkedPost Effect] Running', {
      draftId: currentDraft?.id,
      draftLinkedPostId: currentDraft?.linkedPostId,
      draftLinkedPostMode: currentDraft?.linkedPostMode,
      currentLinkedPostId: linkedPost?.id,
      currentLinkedPostMode: linkedPostMode,
    });

    const fetchLinkedPost = async () => {
      if (!currentDraft?.linkedPostId) {
        // No linked post in this draft - but don't clear if we just set it
        // (this handles the case where linkedPost was set directly via createReplyOrQuoteDraft)
        console.log('[LinkedPost Effect] No linkedPostId on draft');
        return;
      }

      // If we already have the right linked post loaded, skip
      if (linkedPost?.id === currentDraft.linkedPostId && linkedPostMode === currentDraft.linkedPostMode) {
        console.log('[LinkedPost Effect] Already have correct linked post, skipping fetch');
        return;
      }

      // Fetch the linked post
      console.log('[LinkedPost Effect] Fetching linked post:', currentDraft.linkedPostId);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          console.log('[LinkedPost Effect] No auth session available for linked post fetch');
          return;
        }

        const url = buildApiUrl(`/posts/${currentDraft.linkedPostId}`);
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (response.ok) {
          const post = await response.json();
          console.log('[LinkedPost Effect] Fetched linked post:', post);
          setLinkedPostState(post);
          setLinkedPostModeState(currentDraft.linkedPostMode || 'reply');
        } else {
          console.log('[LinkedPost Effect] Fetch failed:', response.status);
        }
      } catch (error) {
        console.error('[LinkedPost Effect] Failed to fetch linked post:', error);
      }
    };

    fetchLinkedPost();
  }, [currentDraft?.id, currentDraft?.linkedPostId, currentDraft?.linkedPostMode, linkedPost?.id, linkedPostMode]);

  const saveDraft = async (lexicalState: string, platforms: { twitter: boolean; bluesky: boolean; farcaster: boolean }) => {
    if (!currentDraft) return;

    try {
      const store = await getStore();
      const updatedDraft: Draft = {
        ...currentDraft,
        raw: lexicalState,
        plainText: extractPlainText(lexicalState),
        updated: Date.now(),
        platforms
      };
      await store.put(updatedDraft);
      setCurrentDraft(updatedDraft);
      setIsNewDraft(false);
    } catch (error) {
      console.error('Error saving draft:', error);
      if (error instanceof StoreError) {
        throw error;
      }
      throw new StoreError('Failed to save draft', 'put', error);
    }
  };

  const loadDraft = useCallback(async (id: string) => {
    try {
      const store = await getStore();
      const draft = await store.get(id);
      if (draft) {
        // Replace only if newer than current selection to avoid snap-back
        setCurrentDraft(prev => {
          if (prev && prev.id === id && prev.updated >= draft.updated) {
            return prev;
          }
          return draft;
        });
        setIsNewDraft(false);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      if (error instanceof StoreError) {
        throw error;
      }
      throw new StoreError('Failed to load draft', 'get', error);
    }
  }, []);

  const loadDraftFromRoute = useCallback(async (id?: string) => {
    if (!id) {
      // Route without id should be handled by the router/RouteEditor
      return;
    }
    try {
      setLastSyncId(id);
      setSyncState('syncing');
      // Optimistic: if we already have it in memory, select it immediately for instant render
      const cached = savedDrafts.find(d => d.id === id);
      if (cached) {
        setCurrentDraft(cached);
        setIsNewDraft(false);
      }
      // Then refresh from the store to ensure latest content
      await loadDraft(id);
      setSyncState('refreshed');
      // Briefly show "refreshed" then hide
      window.setTimeout(() => setSyncState('idle'), 1200);
    } catch (error) {
      console.error('Failed to load draft from route:', error);
      setSyncState('error');
      // Do not auto-create here; guard/router will decide what to do
    }
  }, [savedDrafts, loadDraft]);

  const retryLastSync = useCallback(() => {
    if (lastSyncId) {
      void loadDraftFromRoute(lastSyncId);
    }
  }, [lastSyncId, loadDraftFromRoute]);

  const deleteDraft = async (id: string) => {
    try {
      const store = await getStore();
      await store.remove(id);
      if (currentDraft?.id === id) {
        setCurrentDraft(null);
        setIsNewDraft(true);
        // Clear last opened draft pointer if it was this one (device-local)
        try {
          const last = localStorage.getItem('lw:lastNote');
          if (last === id) localStorage.removeItem('lw:lastNote');
        } catch {}
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      if (error instanceof StoreError) {
        throw error;
      }
      throw new StoreError('Failed to delete draft', 'remove', error);
    }
  };

  const clearAllDrafts = async () => {
    try {
      const store = await getStore();
      const drafts = await store.list();
      await Promise.all(drafts.map(draft => store.remove(draft.id)));
      setCurrentDraft(null);
      setIsNewDraft(true);
      // Clear last opened draft pointer (device-local)
      try { localStorage.removeItem('lw:lastNote'); } catch {}
    } catch (error) {
      console.error('Error clearing all drafts:', error);
      if (error instanceof StoreError) {
        throw error;
      }
      throw new StoreError('Failed to clear all drafts', 'remove', error);
    }
  };

  const clearCurrentDraft = () => {
    setCurrentDraft(null);
    setIsNewDraft(true);
  };

  const exportDrafts = async () => {
    try {
      const store = await getStore();
      const drafts = await store.list();
      const blob = new Blob([JSON.stringify(drafts, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `litewrite-drafts-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting drafts:', error);
      if (error instanceof StoreError) {
        throw error;
      }
      throw new StoreError('Failed to export drafts', 'list', error);
    }
  };

  const importDrafts = async (importedDrafts: Draft[]) => {
    try {
      const store = await getStore();
      const existingDrafts = await store.list();
      const existingIds = new Set(existingDrafts.map(d => d.id));

      // Convert imported drafts to new format if needed
      const convertedDrafts = importedDrafts.map(draft => {
        if (!draft.raw.includes('"root"')) {
          return {
            ...draft,
            raw: createLexicalState(draft.raw),
            plainText: draft.raw,
            updated: draft.updated || Date.now(),
            platforms: draft.platforms || {
              twitter: true,
              bluesky: true,
              farcaster: true
            }
          };
        }
        return {
          ...draft,
          plainText: extractPlainText(draft.raw),
          updated: draft.updated || Date.now(),
          platforms: draft.platforms || {
            twitter: true,
            bluesky: true,
            farcaster: true
          }
        };
      });

      // Save imported drafts, skipping duplicates
      await Promise.all(
        convertedDrafts
          .filter(draft => !existingIds.has(draft.id))
          .map(draft => store.put(draft))
      );
    } catch (error) {
      console.error('Error importing drafts:', error);
      if (error instanceof StoreError) {
        throw error;
      }
      throw new StoreError('Failed to import drafts', 'put', error);
    }
  };

  // Linked post methods for reply/quote functionality
  const setLinkedPost = useCallback((post: PublishedPost, mode: 'reply' | 'quote') => {
    setLinkedPostState(post);
    setLinkedPostModeState(mode);
    // Also update current draft with linked post info for persistence
    if (currentDraft) {
      const updatedDraft: Draft = {
        ...currentDraft,
        linkedPostId: post.id,
        linkedPostMode: mode,
        updated: Date.now(),
      };
      setCurrentDraft(updatedDraft);
      // Persist to store
      getStore().then(store => store.put(updatedDraft));
    }
  }, [currentDraft]);

  const clearLinkedPost = useCallback(() => {
    setLinkedPostState(null);
    setLinkedPostModeState(null);
    // Also clear from current draft
    if (currentDraft) {
      const updatedDraft: Draft = {
        ...currentDraft,
        linkedPostId: undefined,
        linkedPostMode: undefined,
        updated: Date.now(),
      };
      setCurrentDraft(updatedDraft);
      // Persist to store
      getStore().then(store => store.put(updatedDraft));
    }
  }, [currentDraft]);

  const toggleLinkedPostMode = useCallback(() => {
    if (!linkedPostMode) return;
    const newMode = linkedPostMode === 'reply' ? 'quote' : 'reply';
    setLinkedPostModeState(newMode);
    // Update current draft
    if (currentDraft) {
      const updatedDraft: Draft = {
        ...currentDraft,
        linkedPostMode: newMode,
        updated: Date.now(),
      };
      setCurrentDraft(updatedDraft);
      // Persist to store
      getStore().then(store => store.put(updatedDraft));
    }
  }, [linkedPostMode, currentDraft]);

  return (
    <DraftContext.Provider
      value={{
        currentDraft,
        savedDrafts,
        isNewDraft,
        areDraftsLoading,
        syncState,
        retryLastSync,
        saveDraft,
        loadDraft,
        loadDraftFromRoute,
        deleteDraft,
        clearCurrentDraft,
        clearAllDrafts,
        createNewDraft,
        createDraftWithText,
        createReplyOrQuoteDraft,
        exportDrafts,
        importDrafts,
        linkedPost,
        linkedPostMode,
        setLinkedPost,
        clearLinkedPost,
        toggleLinkedPostMode,
      }}
    >
      {children}
    </DraftContext.Provider>
  );
} 
